// Pure LTO wire-JSON builder/parser, plus the localStorage testing override for
// expiration_time_ms's number-vs-string ambiguity. Mirrors ../carousel/buildCarouselTemplateJson.js's
// activeCarouselWireShape() pattern.
import { buildButtonComponents, parseButtonComponents } from './buildButtonComponents';

// Two internal integrations disagree on whether expiration_time_ms belongs on the wire as a
// number or a numeric string. Default false (number) — flip via the localStorage override below
// to test the other side without a redeploy.
export const LTO_EXPIRY_AS_STRING = false;

// Temporary test switch: set with localStorage.setItem('br_lto_expiry_as_string','true') in the
// widget iframe's console, then re-save the node. Remove once the live test settles the default.
export function activeLtoExpiryAsString() {
  try {
    // localStorage can throw in sandboxed iframes — fall back to the constant.
    const override = window.localStorage.getItem('br_lto_expiry_as_string');
    if (override === 'true') return true;
    if (override === 'false') return false;
  } catch {
    // fall through to the constant below
  }
  return LTO_EXPIRY_AS_STRING;
}

/**
 * @typedef {object} BuildLtoTemplateJsonInput
 * @property {boolean} hasExpiration
 * @property {(number|null)} [expirationMs] - epoch ms; read only when hasExpiration, written empty when not a number
 * @property {import('../shared/templateModel').TemplateButtonInfo[]} buttons
 * @property {Object.<number, string>} values - per-button values, see buildButtonComponents.js
 * @property {boolean} [expiryAsString] - defaults to activeLtoExpiryAsString()
 */

/**
 * Builds the LTO wire `{ components: [...] }`. The limited_time_offer component is included only
 * when hasExpiration is true — omitting it when it's needed silently drops the countdown, so this
 * stays symmetric rather than "always include, zero when absent".
 * @param {BuildLtoTemplateJsonInput} input
 * @returns {object}
 */
export function buildLtoTemplateJson({
  hasExpiration, expirationMs, buttons, values, expiryAsString,
}) {
  const components = [];
  if (hasExpiration) {
    const asString = expiryAsString !== undefined ? expiryAsString : activeLtoExpiryAsString();
    // A draft with no expiry yet carries '' (what parseLtoTemplateJson reads back as none), not 0 or "null".
    let expirationValue = '';
    if (Number.isFinite(expirationMs)) expirationValue = asString ? String(expirationMs) : expirationMs;
    components.push({
      type: 'limited_time_offer',
      parameters: [{ type: 'limited_time_offer', limited_time_offer: { expiration_time_ms: expirationValue } }],
    });
  }
  components.push(...buildButtonComponents(buttons, values));
  return { components };
}

/**
 * Inverse of buildLtoTemplateJson, for restore.
 * @param {object} waTemplateJson - saved wire JSON
 * @param {import('../shared/templateModel').TemplateButtonInfo[]} buttons
 * @returns {{ expirationMs: (number|null), values: Object.<number, string> }}
 */
export function parseLtoTemplateJson(waTemplateJson, buttons) {
  const components = Array.isArray(waTemplateJson?.components) ? waTemplateJson.components : [];
  const ltoComponent = components.find((component) => component?.type === 'limited_time_offer');
  const raw = ltoComponent?.parameters?.[0]?.limited_time_offer?.expiration_time_ms;
  const expirationMs = (raw === undefined || raw === null || raw === '') ? null : Number(raw);
  return {
    expirationMs: Number.isNaN(expirationMs) ? null : expirationMs,
    values: parseButtonComponents(components, buttons),
  };
}
